import React from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogIn, LogOut, UserCircle, Instagram } from "lucide-react";

const navLinkClass = ({ isActive }) =>
  `px-3 py-2 rounded-full text-sm transition-colors ${
    isActive ? "text-[#2C4C3B] font-medium bg-[#E6F0E9]" : "text-[#52606D] hover:text-[#2C4C3B]"
  }`;

const HeaderAuthControls = () => {
  const { user, openAuth, logout } = useAuth();
  const isGuest = !user || user.is_guest;
  if (isGuest) {
    return (
      <button
        onClick={() => openAuth({ mode: "signin" })}
        className="ml-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-[#2C4C3B] text-white hover:bg-[#1F3A2C] transition-colors"
        data-testid="header-signin-btn">
        <LogIn className="w-4 h-4" /> Sign in
      </button>
    );
  }
  return (
    <div className="ml-2 flex items-center gap-2" data-testid="header-user-menu">
      <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E6F0E9] text-[#2C4C3B] text-sm">
        <UserCircle className="w-4 h-4" />
        <span className="font-medium" data-testid="header-user-name">{user.name?.split(" ")[0] || "Account"}</span>
      </div>
      <button
        onClick={logout}
        title="Sign out"
        className="w-9 h-9 rounded-full bg-[#F4F5F0] hover:bg-[#E6F0E9] flex items-center justify-center text-[#52606D]"
        data-testid="header-logout-btn">
        <LogOut className="w-4 h-4" />
      </button>
    </div>
  );
};

export const SiteHeader = () => {
  const { user } = useAuth();
  return (
    <header className="glass-nav sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <div className="leading-tight">
            <div className="font-display text-xl font-semibold text-[#2C4C3B]">CommPass</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#52606D] -mt-0.5">by Sensiiq</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/" end className={navLinkClass} data-testid="nav-home">Home</NavLink>
          <NavLink to="/search" className={navLinkClass} data-testid="nav-programming">Programming</NavLink>
          <NavLink to="/goals" className={navLinkClass} data-testid="nav-goals">Goals</NavLink>
          <NavLink to="/plans" className={navLinkClass} data-testid="nav-plans">Plans</NavLink>
          <NavLink to="/clinical-notes" className={navLinkClass} data-testid="nav-more-info">More Info</NavLink>
          <NavLink to="/about" className={navLinkClass} data-testid="nav-about">About Us</NavLink>
          {user?.role === "admin" && (
            <NavLink to="/admin/users" className={navLinkClass} data-testid="nav-admin">Admin</NavLink>
          )}
          <HeaderAuthControls />
        </nav>

        <div className="md:hidden">
          <HeaderAuthControls />
        </div>
      </div>
    </header>
  );
};

export const SiteFooter = () => (
  <footer className="bg-[#F4F5F0] border-t border-[#E2E8F0] mt-24" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-10">
      <div>
        <div className="flex items-center mb-3">
          <div className="leading-tight">
            <div className="font-display text-xl font-semibold text-[#2C4C3B]">CommPass</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#52606D] -mt-0.5">by Sensiiq</div>
          </div>
        </div>
        <p className="text-sm text-[#52606D] max-w-sm leading-relaxed">
          A clinical decision-support tool helping SLPs, educators and AAC specialists generate
          personalised AAC programming and SMART goal banks.
        </p>
      </div>
      <div>
        <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#52606D] mb-4">Explore</h4>
        <ul className="space-y-2 text-sm text-[#2C4C3B]">
          <li><Link to="/" className="hover:underline">Home</Link></li>
          <li><Link to="/search" className="hover:underline">Programming</Link></li>
          <li><Link to="/goals" className="hover:underline">Goals</Link></li>
          <li><Link to="/plans" className="hover:underline">Plans</Link></li>
          <li><Link to="/clinical-notes" className="hover:underline">More Info</Link></li>
          <li><Link to="/about" className="hover:underline">About Us</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#52606D] mb-4">Credits</h4>
        <p className="text-sm text-[#1A202C] leading-relaxed">
          Developed by <span className="font-medium">Sadiya Yasin</span>
          <br />
          <span className="text-[#52606D]">SLP, CEO &amp; Founder — Sensiiq</span>
        </p>
        <p className="text-sm mt-3 text-[#52606D]">
          A product of <span className="font-medium text-[#C66A51]">Sensiiq</span>
        </p>
        <a
          href="https://instagram.com/sens.iiq"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-2 text-sm text-[#2C4C3B] hover:text-[#C66A51] transition-colors group"
          data-testid="footer-instagram-link">
          <span className="w-8 h-8 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center group-hover:border-[#C66A51] group-hover:bg-[#FDF6F0] transition-colors">
            <Instagram className="w-4 h-4" />
          </span>
          <span className="font-medium">@sens.iiq</span>
        </a>
      </div>
    </div>
    <div className="border-t border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-6 py-5 text-xs text-[#52606D] flex flex-wrap items-center justify-between gap-3">
        <span>© {new Date().getFullYear()} Sensiiq. CommPass is a clinical reference tool.</span>
        <span>For clinical/educational use. Not a substitute for individualised assessment.</span>
      </div>
    </div>
  </footer>
);
